<template>
  <div class="container mx-auto">
    <div>
      <div class="mb-6">
        <doe-page-heading label="Welcome, let’s get started!" class="mb-4" />
        <doe-paragraph
          class="mb-2"
          description="You will need to register for a personal Department of Education (DoE) account to access the Self-Assessment and Quality Improvement Planning 
Portal. If you already have a DoE account, go to the previous page and click log in."
        />
        <doe-paragraph
          class="mb-2"
          description="The following steps are for centre directors or nominated supervisors only. If you are an early childhood educator or teacher, please contact your centre director to request access."
        />
      </div>

      <doe-fieldset-heading
        class="mb-6"
        label="Do you have a DoE account already?"
        :description="[
          'If you have an existing DoE account, return to the previous page and click log in.',
          'If you are unsure whether you have an existing DoE account, please contact the department\'s [Information and enquiries team](https://education.nsw.gov.au/early-childhood-education/about-us/contact-us).'
        ]"
      />

      <doe-field-heading
        label="Here’s what you’ll need to proceed"
        class="mb-4"
      />
      <ol>
        <doe-stepper-block :step="1" :completed="false">
          <h5 class="font-bold font-sans text-blue-900 mb-1">
            Enter your name and contact details
          </h5>
          <p class="font-sans text-blue-900">
            We’ll need your name and contact information to create a DoE account
            for you. This includes your personal (not shared) email address.
          </p>
        </doe-stepper-block>
        <doe-stepper-block :step="2" :completed="false">
          <h5 class="font-bold font-sans text-blue-900 mb-1">
            Confirm your identity
          </h5>
          <p class="font-sans text-blue-900">
            We will need to confirm your identity using two forms of identity
            documents. Please ensure you have access to any two of the following
            documents:
          </p>
          <ol class="list-disc pl-4 mt-2">
            <li class="font-sans text-sm text-blue-900 mb-2">
              Australian passport
            </li>
            <li class="font-sans text-sm text-blue-900 mb-2">
              Australian birth certificate
            </li>
            <li class="font-sans text-sm text-blue-900 mb-2">
              Australian citizenship certificate
            </li>
            <li class="font-sans text-sm text-blue-900 mb-2">
              ImmiCard
            </li>
            <li class="font-sans text-sm text-blue-900 mb-2">
              Medicare card
            </li>
            <li class="font-sans text-sm text-blue-900 mb-2">
              Australian visa (foreign passports)
            </li>
            <li class="font-sans text-sm text-blue-900">
              Australian driver licence.
            </li>
          </ol>
        </doe-stepper-block>
        <doe-stepper-block :step="3" :completed="false">
          <h5 class="font-bold font-sans text-blue-900 mb-1">
            Register your service/s
          </h5>
          <p class="font-sans text-blue-900">
            We'll need to verify and link you to any services you manage. You will need access to the service email address listed in
            <a
              class="underline"
              href="https://public.nqaits.acecqa.gov.au/Pages/Landing.aspx"
              target="_blank"
              >NQA ITS<span class="sr-only">Opens external website</span></a
            >.
          </p>
        </doe-stepper-block>
        <doe-stepper-block :step="4" :completed="false" :finalStep="true">
          <h5 class="font-bold font-sans text-blue-900 mb-1">Add your educators</h5>
          <p class="font-sans text-blue-900 mb-2">
            Adding educators will allow them to access the Self-Assessment and Quality Improvement Planning Portal on behalf of your service.
          </p>
          <p class="font-sans text-blue-900">You will need the educator's:</p>
          <ol class="list-disc pl-4 mt-2 mb-4">
            <li class="font-sans text-sm text-blue-900 mb-2">
              Consent to create a DoE account on their behalf
            </li>
            <li class="font-sans text-sm text-blue-900 mb-2">
              Name and date of birth
            </li>
            <li class="font-sans text-sm text-blue-900 mb-2">
              Personal (not shared) email address.
            </li>
          </ol>
        </doe-stepper-block>
      </ol>
      <div class="pb-6">
        <doe-paragraph
          class="my-2"
          description="The registration process will take 5-10 minutes."
        />
      </div>
      <form-generator
        :schema="schema"
        @form:previous="formPrevious()"
        @form:next="formNext()"
      />
    </div>
  </div>
</template>

<script>
import schema from "@/schemas/register/registerOnboarding";
import FormGenerator from "@/components/form/FormGenerator";
import DoePageHeading from "@/components/typography/PageHeading";
import DoeFieldHeading from "@/components/typography/FieldHeading";
import DoeFieldsetHeading from "@/components/typography/FieldsetHeading";
import DoeParagraph from "@/components/typography/Paragraph";
import DoeStepperBlock from "@/components/typography/StepperBlock";

export default {
  name: "AuthOnboarding",
  inject: {
    $validator: "$validator"
  },
  components: {
    FormGenerator,
    DoePageHeading,
    DoeFieldHeading,
    DoeFieldsetHeading,
    DoeParagraph,
    DoeStepperBlock
  },
  data() {
    return {
      schema: schema,
      slickOptions: {
        slidesToShow: 1,
        nextArrow: false,
        prevArrow: false,
        dots: false
      }
    };
  },
  methods: {
    login() {
      this.$router.push({ name: "auth-login" });
    },
    sliderNext() {
      this.$refs.slick.next();
    },
    sliderPrevious() {
      this.$refs.slick.prev();
    },
    formPrevious() {
      this.$router.push({ name: "landing" });
    },
    formNext() {
      this.$router.push({ name: "register-account" });
    }
  }
};
</script>

<style lang="scss" scoped>
ul {
  text-align: center;
  overflow: auto;
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  li {
    background: #b3b3b3;
    display: inline-block;
    float: left;
    height: 10px;
    width: 10px;
    margin: 0 10px;
    text-indent: -9999px;
    border-radius: 100%;
  }
}
p /deep/ a {
  @apply inline-block relative underline;
}
p /deep/ a[href*="//"]:not([href*="localhost.com"]) {
  padding-right: 1.5rem;
  &:after {
    @apply absolute block;
    content: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 100 89'><path d='M100,4.16666667 L100,26.3861111 C100,30.1119792 95.4930556,31.9381944 92.8869792,29.3324653 L86.6878472,23.1329861 L44.4095486,65.4112847 C42.7822917,67.0385417 40.1442708,67.0385417 38.5170139,65.4112847 L34.5887153,61.4829861 C32.9614583,59.8557292 32.9614583,57.2175347 34.5887153,55.5904514 L76.8673611,13.3118056 L70.6689236,7.11302083 C68.0524306,4.49652778 69.9055556,0 73.6152778,0 L95.8333333,0 C98.1345486,0 100,1.86545139 100,4.16666667 Z M70.6647569,47.0128472 L67.8869792,49.790625 C67.1055707,50.5720468 66.6666073,51.6318913 66.6666667,52.7369792 L66.6666667,77.7777778 L11.1111111,77.7777778 L11.1111111,22.2222222 L56.9444444,22.2222222 C58.049457,22.2221592 59.1092025,21.783211 59.890625,21.0019097 L62.6684028,18.2241319 C65.2932292,15.5991319 63.4342014,11.1111111 59.7222222,11.1111111 L8.33333333,11.1111111 C3.73090278,11.1111111 0,14.8420139 0,19.4444444 L0,80.5555556 C0,85.1579861 3.73090278,88.8888889 8.33333333,88.8888889 L69.4444444,88.8888889 C74.046875,88.8888889 77.7777778,85.1579861 77.7777778,80.5555556 L77.7777778,49.9590278 C77.7777778,46.246875 73.2895833,44.3878472 70.6647569,47.0128472 Z' /></svg>");
    top: 0px;
    right: 0px;
    width: 1.25rem;
    height: 1.25rem;
  }
}
</style>
